/** @forge-header
 * @id          ncaaParsers
 * @role        NCAA ticker parsers | men's/women's basketball game moneylines
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo | kalshiTicker/helpers:parseFallback
 * @downstream  kalshiTicker/parse
 * @exports     parseNcaaMbGame:fn | parseNcaaWbGame:fn | parseNcaaGame:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, splitAnchoredCompetitors } from '../helpers';

/** NCAA Men's Basketball: KXNCAAMBGAME-26JAN28UCLAORE-UCLA */
export function parseNcaaMbGame(raw: string): ParsedTicker {
  return parseNcaaGameGeneric(raw, 'KXNCAAMBGAME', 'NCAAMB');
}

/** NCAA Women's Basketball: KXNCAAWBGAME-... */
export function parseNcaaWbGame(raw: string): ParsedTicker {
  return parseNcaaGameGeneric(raw, 'KXNCAAWBGAME', 'NCAAWB');
}

/** NCAA generic game: KXNCAAGAME-... */
export function parseNcaaGame(raw: string): ParsedTicker {
  return parseNcaaGameGeneric(raw, 'KXNCAAGAME', 'NCAA');
}

/** Generic NCAA game parser */
function parseNcaaGameGeneric(raw: string, series: string, label: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) {
    return parseFallback(raw);
  }

  const mid = parts[1] ?? '';
  const teamCode = parts[2] ?? null;
  // College team codes are 2-5 letters (2026-10-28: KXNCAAMBGAME-26NOV02PCJAC — PC @ JAC — read as the raw ticker
  // because the pack was 5 letters, and a fixed 3+3 split misreads any other width). Split on the outcome's own code,
  // as tennis does; a fixed 3+3 only when there is no outcome to anchor on.
  if (mid.length < 9) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const teamCodes = mid.slice(7);
  const anchored = splitAnchoredCompetitors(teamCodes, teamCode);
  if (!anchored && teamCodes.length < 6) {
    return parseFallback(raw);
  }

  const [awayCode, homeCode] = anchored ?? [teamCodes.slice(0, 3), teamCodes.slice(3, 6)];

  const game: GameInfo = { awayCode, homeCode };

  const shortLabel = `${label}: ${awayCode} @ ${homeCode}`;
  const fullLabel = teamCode
    ? `${label}: ${awayCode} @ ${homeCode} · ${teamCode} Win`
    : `${label}: ${awayCode} @ ${homeCode}`;

  return {
    raw,
    category: 'ncaa-game',
    series,
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: teamCode,
    shortLabel,
    fullLabel,
  };
}
